import React, { useEffect } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLazyDataModel } from '@thd-nucleus/data-sources';
import {
  Button,
  LoadingButton,
  DrawerBody,
  DrawerFooter,
  Alert,
} from '@one-thd/sui-atomic-components';
import { getAddressFormValues } from '../../../../../utils/forms';
import { IMappedAddressForm } from '../../../../../models/common/address-form';

import {
  INSTANT_CHECKOUT_BUTTON_SUBMISSION,
  INSTANT_CHECKOUT_OVERLAY,
  POST_PAGE_ERROR,
} from '../../../../../constants/analytics';
import { track } from '../../../../../analytics/analytics';

import { validationSchema } from '../../../../../utils/validation-rules/address-validation';
import { useDrawer } from '../../../../../hooks/use-drawer';
import { InstantCheckoutDrawerHeader } from '../../../InstantCheckoutDrawerHeader';
import { AddressFormFields } from '../InstantCheckoutBillingDelivery/AddressFormFields';
import { ADDRESS_FORM_DATA_TEST_IDS } from '../../../../../constants/address-constants';
import { useAddress } from '../../../../../hooks/use-address';
import { usePayments } from '../../../../../hooks/use-payments';
import { useViews } from '../../../../../hooks/use-views';
import {
  createNewRelicActions,
  logNewRelicAction,
} from '../../../../../utils/newrelic';
import {
  ADD_PAYMENT,
  SELECT_PAYMENT,
  CONFIRM_PAYMENT,
} from '../../../../../models/views';
import {
  orangePayToken,
  updateAddresses,
  updateInstantCheckoutSettings,
} from '../../../../../models/graphQLModels';
import { IAddress, IPhone } from '../../../../../models/address';
import { getChannelID, redirectToSignIn } from '../../../../../utils/general';
import { useInstantCheckoutStatus } from '../../../../../hooks/use-instant-checkout-status';
import {
  ERR_REDIRECT,
  ERR_UNABLE_TO_SAVE,
} from '../../../../../constants/response-error-messages';
import { NON_DIGITS_REGEX } from '../../../../../constants/regex';
import { TAlertStatus, TAlertType } from '../../../../../models/common/alert';
import { getIsAuthError } from '../../../../../constants/error-codes';

export const EditAddressView = () => {
  const { hideDrawer } = useDrawer();
  const { defaultAddress, setSelectedAddress } = useAddress();
  const { goToView, currentView } = useViews();
  const { hasPayments, defaultPayment } = usePayments();
  const actions = createNewRelicActions(currentView);
  const { userId, customerAccountId, setAlertMessage } =
    useInstantCheckoutStatus();
  const defaultValues = getAddressFormValues(defaultAddress);
  const formMethods = useForm<IMappedAddressForm>({
    defaultValues,
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
  });

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);

  const {
    handleSubmit,
    formState: { isDirty },
  } = formMethods;

  const [callUpdateAddress, data] = useLazyDataModel('updateAddresses');
  const { called, loading: updateLoading, error, data: updateResult } = data;

  useEffect(() => {
    setHasError(!!error);
    setIsSubmitting(updateLoading);

    if (updateResult && called && !updateLoading && !error) {
      const address: IAddress = {
        id: updateResult?.updateAddresses?.addresses[0].id,
        isDefault: updateResult?.updateAddresses?.addresses[0]?.isDefault,
        isInstantCheckoutDefault:
          updateResult?.updateAddresses?.addresses[0]?.isInstantCheckoutDefault,
        nickName: updateResult?.updateAddresses?.addresses[0]?.nickName,
        lastModifiedDate:
          updateResult?.updateAddresses?.addresses[0]?.lastModifiedDate,
        name: {
          firstName:
            updateResult?.updateAddresses?.addresses[0]?.name?.firstName,
          lastName: updateResult?.updateAddresses?.addresses[0]?.name?.lastName,
        },
        phones: updateResult?.updateAddresses?.addresses[0]?.phones?.map(
          (phone: IPhone): IPhone => {
            return {
              number: phone?.number,
              id: phone?.id,
              isPrimary: phone?.isPrimary ?? false,
            };
          },
        ),
        postalAddress: {
          addressLine1:
            updateResult?.updateAddresses?.addresses[0]?.postalAddress
              ?.addressLine1,
          addressLine2:
            updateResult?.updateAddresses?.addresses[0]?.postalAddress
              ?.addressLine2,
          city: updateResult?.updateAddresses?.addresses[0]?.postalAddress
            ?.city,
          country:
            updateResult?.updateAddresses?.addresses[0]?.postalAddress?.country,
          stateProvince:
            updateResult?.updateAddresses?.addresses[0]?.postalAddress
              ?.stateProvince,
          zipCode:
            updateResult?.updateAddresses?.addresses[0]?.postalAddress?.zipCode,
        },
      };
      setSelectedAddress(address);
      logNewRelicAction(actions.EDIT_ADDRESS);
      if (defaultPayment) {
        goToView(CONFIRM_PAYMENT);
      } else if (hasPayments()) {
        track(INSTANT_CHECKOUT_OVERLAY, {
          value: 'instant checkout select payment method',
        });
        goToView(SELECT_PAYMENT);
      } else {
        track(INSTANT_CHECKOUT_OVERLAY, {
          value: 'instant checkout add new payment',
        });
        goToView(ADD_PAYMENT);
      }
    }
    if (error) {
      logNewRelicAction(actions.EDIT_ADDRESS, error);
      if (getIsAuthError(error)) {
        setAlertMessage({
          alertType: TAlertType.GENERAL,
          alertStatus: TAlertStatus.INFO,
          text: ERR_REDIRECT.message,
        });
        redirectToSignIn();
      }

      track(POST_PAGE_ERROR, {
        pageSection: 'add address overlay',
        message: ERR_UNABLE_TO_SAVE.message,
        code: error?.code || 'n/a',
        type: 'error',
        displayType: 'inline',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, updateResult, called, updateLoading]);

  const onSubmitForm: SubmitHandler<IMappedAddressForm> = (values) => {
    track(INSTANT_CHECKOUT_BUTTON_SUBMISSION, {
      overlayType: 'edit address',
      action: 'save and continue',
    });
    if (!isDirty) {
      hideDrawer();
      return;
    }

    const cityState = values.cityState.split(',');
    const city = cityState[0]?.trim();
    const state = cityState[1]?.trim();
    const phone = values.primaryPhone.replace(NON_DIGITS_REGEX, '');

    if (!defaultAddress) {
      setHasError(true);
      return;
    }

    const updatedAddress: IAddress = {
      ...defaultAddress,
      postalAddress: {
        ...defaultAddress.postalAddress,
        addressLine1: values.addressLine1,
        addressLine2: values.addressLine2 || null,
        city,
        stateProvince: state,
        zipCode: values.zipCode,
      },
      name: {
        firstName: values.firstName,
        lastName: values.lastName,
      },
      phones: [
        {
          number: phone,
          id: defaultAddress?.phones[0]?.id,
          isPrimary: defaultAddress?.phones[0]?.isPrimary ?? false,
        },
      ],
    };
    setIsSubmitting(true);
    callUpdateAddress({
      variables: {
        userId,
        customerAccountId,
        channelId: getChannelID(),
        addresses: [updatedAddress],
      },
      ssr: false,
      fetchPolicy: 'no-cache',
      context: {
        withAuth: true,
      },
    });
  };

  useEffect(() => {
    if (isSubmitting && hasError && error) {
      logNewRelicAction(actions.EDIT_DELIVERY_DETAILS, error);
    }
  }, [isSubmitting]);

  useEffect(() => {
    if (isSubmitting && updateResult && !error) {
      logNewRelicAction(actions.EDIT_DELIVERY_DETAILS);
    }
  }, [isSubmitting]);

  const onCancel = () => {
    track(INSTANT_CHECKOUT_BUTTON_SUBMISSION, {
      overlayType: 'edit address',
      action: 'cancel',
    });
    hideDrawer();
  };

  return (
    <div>
      <InstantCheckoutDrawerHeader
        title="Edit Delivery Details"
        onClose={onCancel}
      />

      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmitForm)} className="sui-pt-8">
          <DrawerBody>
            <AddressFormFields />
            {hasError && (
              <Alert status="error">{ERR_UNABLE_TO_SAVE.message}</Alert>
            )}
          </DrawerBody>
          <DrawerFooter>
            <div className="sui-grid sui-grid-cols-1 sui-gap-4 sm:sui-grid-cols-2">
              <div className="sui-order-last sm:sui-order-first">
                <Button
                  fullWidth
                  variant="secondary"
                  data-testid={ADDRESS_FORM_DATA_TEST_IDS.CANCEL}
                  onClick={onCancel}
                >
                  Cancel
                </Button>
              </div>
              <LoadingButton
                loading={isSubmitting}
                fullWidth
                data-testid={ADDRESS_FORM_DATA_TEST_IDS.SAVE_AND_CONTINUE}
                variant="primary"
                type="submit"
              >
                Save and Continue
              </LoadingButton>
            </div>
          </DrawerFooter>
        </form>
      </FormProvider>
    </div>
  );
};

EditAddressView.dataModel = {
  updateAddresses,
  updateInstantCheckoutSettings,
  orangePayToken,
};
